import { createSlice } from "@reduxjs/toolkit";
import { resetAll } from "./baseSlice";
import { toast } from "react-toastify";
import intl from "../../i18n/intl";

const initialState = {
  data: {
    list: [],
    totalCount: 0,
  },
  record: null,
  // associations will be stored inside record.associations when fetched
  requesting: false,
  error: null,
  action: null,
  query: {
    pageNo: 1,
    pageSize: 50,
    sortBy: "asc",
    sortColumn: "name",
    namePattern: "",
  },
  associationsQuery: {
    pageNo: 1,
    pageSize: 10,
    sortBy: "asc",
    sortColumn: "entityName",
    entityName: "",
  },
};

const customIntl = intl();

const rewardSlice = createSlice({
  name: "rewards",
  initialState,
  extraReducers: (builder) => builder.addCase(resetAll, () => initialState),
  reducers: {
    listRequest: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.action = "list";
      state.record = null;
    },
    listSuccess: (state, action) => {
      state.data = action.payload.response;
      state.requesting = false;
      state.error = null;
    },
    listFail: (state, action) => {
      state.data = {
        list: [],
        totalCount: 0,
      };
      state.requesting = false;
      state.error = action.payload;
    },
    setQuery: (state, action) => {
      state.query = {
        ...state.query,
        ...action.payload,
      };
    },
    createRequest: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.action = "create";
    },
    createSuccess: (state, action) => {
      state.requesting = false;
      state.error = null;
      state.action = "create";
      toast.success(customIntl.formatMessage({ id: "reward_create_success" }));
    },
    createFail: (state, action) => {
      state.requesting = false;
      state.error = action.payload;
      if (action.payload.status !== 400 && action.payload.status !== 401) {
        toast.error(customIntl.formatMessage({ id: "something_else_wrong" }));
      }
    },
    detailsRequest: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.action = "details";
      state.record = null;
    },
    detailsSuccess: (state, action) => {
      state.record = action.payload.response;
      state.associationsQuery = initialState.associationsQuery;
      state.requesting = false;
      state.error = null;
    },
    detailsFail: (state, action) => {
      state.record = null;
      state.requesting = false;
      state.error = action.payload;
      toast.error(customIntl.formatMessage({ id: "something_else_wrong" }));
    },
    updateDetailRequest: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.action = "update";
    },
    updateDetailSuccess: (state, action) => {
      state.requesting = false;
      state.error = null;
      state.action = "update";
      toast.success(customIntl.formatMessage({ id: "reward_update_success" }));
    },
    updateDetailFail: (state, action) => {
      state.requesting = false;
      state.error = action.payload;
      if (action.payload.status !== 400 && action.payload.status !== 401) {
        toast.error(customIntl.formatMessage({ id: "something_else_wrong" }));
      }
    },
    associationsRequest: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.action = "associations";
    },
    associationsSuccess: (state, action) => {
      state.requesting = false;
      state.error = null;
      state.record = {
        ...(state.record || {}),
        associations: action.payload.response?.list || [],
        associationsTotalCount: action.payload.response?.totalCount || 0,
      };
      if (action.payload.query) {
        state.associationsQuery = {
          ...state.associationsQuery,
          ...action.payload.query,
        };
      }
    },
    associationsFail: (state, action) => {
      state.requesting = false;
      state.error = action.payload;
    },
    setAssociationsQuery: (state, action) => {
      state.associationsQuery = {
        ...state.associationsQuery,
        ...action.payload,
      };
    },
    deleteRequest: (state, action) => {
      state.requesting = true;
      state.error = null;
      state.action = "delete";
    },
    deleteSuccess: (state, action) => {
      state.requesting = false;
      state.error = null;
      state.action = "delete";
      toast.success(customIntl.formatMessage({ id: "reward_delete_success" }));
    },
    deleteFail: (state, action) => {
      state.requesting = false;
      state.error = action.payload;
      if (action.payload.status !== 400 && action.payload.status !== 401) {
        toast.error(customIntl.formatMessage({ id: "something_else_wrong" }));
      }
    },
  },
});

export const {
  createFail,
  createRequest,
  createSuccess,
  detailsFail,
  detailsRequest,
  detailsSuccess,
  listFail,
  listRequest,
  listSuccess,
  setQuery,
  updateDetailFail,
  updateDetailRequest,
  updateDetailSuccess,
  deleteRequest,
  deleteSuccess,
  deleteFail,
  associationsRequest,
  associationsSuccess,
  associationsFail,
  setAssociationsQuery,
} = rewardSlice.actions;
export default rewardSlice.reducer;
